import { useEffect, useState } from 'react'
import { fetchBlob } from '../api/client'

/**
 * Profilbilleder kræver et Authorization-header og kan derfor ikke sættes direkte
 * i <img src>. Billedet hentes én gang som blob og genbruges som object-URL.
 *
 * Cachen er nøglet på billedets URL, og URL'en indeholder et versionsnummer fra
 * serveren (?v=...). Skifter en bruger sit billede, ændres nøglen automatisk, og
 * det gamle billede bliver ikke vist. Object-URL'erne lever sessionen ud - de
 * fylder ubetydeligt, da der højst er ét pr. bruger.
 */
const objectUrls = new Map<string, string | null>()
const pending = new Map<string, Promise<string | null>>()

async function load(photoUrl: string): Promise<string | null> {
  const blob = await fetchBlob(photoUrl)
  const objectUrl = blob ? URL.createObjectURL(blob) : null
  objectUrls.set(photoUrl, objectUrl)
  pending.delete(photoUrl)
  return objectUrl
}

/** Fjerner et billede fra cachen, f.eks. når brugeren lige har skiftet det. */
export function forgetPhoto(photoUrl?: string | null) {
  if (!photoUrl) return

  const existing = objectUrls.get(photoUrl)
  if (existing) URL.revokeObjectURL(existing)

  objectUrls.delete(photoUrl)
  pending.delete(photoUrl)
}

/**
 * Henter et beskyttet billede fra API-et og returnerer en object-URL, der kan
 * sættes i <img src>. Null indtil billedet er hentet - eller hvis det ikke findes.
 *
 * Bruges til sagernes dokumentationsfotos, hvor stien altid peger på API-et.
 * Cachen er den samme som til profilbilleder: en fil har et fast id, så indholdet
 * på en given sti ændrer sig ikke.
 */
export function useAuthedBlob(path: string): string | null {
  const [url, setUrl] = useState<string | null>(() => objectUrls.get(path) ?? null)

  useEffect(() => {
    // Tom sti = der er ikke noget at hente endnu (f.eks. et billede der ikke er taget).
    if (!path) {
      setUrl(null)
      return
    }

    if (objectUrls.has(path)) {
      setUrl(objectUrls.get(path) ?? null)
      return
    }

    let cancelled = false
    const request = pending.get(path) ?? load(path)
    pending.set(path, request)

    void request.then((result) => {
      if (!cancelled) setUrl(result)
    })

    return () => {
      cancelled = true
    }
  }, [path])

  return url
}

/**
 * Returnerer en visbar URL til et profilbillede, eller null hvis brugeren ikke
 * har et (så viser UI'et initialer i stedet).
 */
export function useUserPhoto(photoUrl?: string | null): string | null {
  const isApiPath = Boolean(photoUrl?.startsWith('/'))
  const [url, setUrl] = useState<string | null>(() =>
    !photoUrl ? null : isApiPath ? (objectUrls.get(photoUrl) ?? null) : photoUrl
  )

  useEffect(() => {
    if (!photoUrl) {
      setUrl(null)
      return
    }

    // Absolut URL (f.eks. et eksternt billede) kan bruges som den er.
    if (!photoUrl.startsWith('/')) {
      setUrl(photoUrl)
      return
    }

    if (objectUrls.has(photoUrl)) {
      setUrl(objectUrls.get(photoUrl) ?? null)
      return
    }

    let cancelled = false
    const request = pending.get(photoUrl) ?? load(photoUrl)
    pending.set(photoUrl, request)

    void request.then((result) => {
      if (!cancelled) setUrl(result)
    })

    return () => {
      cancelled = true
    }
  }, [photoUrl])

  return url
}
